// The Ongoing a mail thread belongs to (acc-account-sync-srv#11, AI Brain spec §9).
//
// The spec's rule is that relationships attach to the THREAD, and that a reply to a confirmed thread
// inherits that relationship without being asked about again. From the app's side that rule is only
// real if you can SEE it: opening a thread has to say "this is part of <story>", or the user has no
// way to know their confirmation stuck — and no way to get from the mail back to the story it
// belongs to.
//
// The edges themselves live in busy-timeline-srv's relationship graph (busy-timeline-srv#19) as
// Ongoing –Contains→ Timeline, and `GET v1/objects/timeline/{id}` returns them as neighbours. This
// module is the reading of that payload and nothing else: no store, no axios, no Vue, so every rule
// below can be checked on its own.

/** Graph edges point Ongoing → Timeline, so from the THREAD's side the edge always points IN. */
const INBOUND = 'in'

/** Case-insensitive compare that tolerates the wire's PascalCase enums ("Confirmed", "Contains"). */
const is = (value, expected) => String(value || '').toLowerCase() === expected

/**
 * The Ongoing edges on a resolved timeline, newest-confirmed first.
 *
 * Rejected edges are dropped outright: a rejection is the record that this thread was OFFERED a
 * story and turned it down (busy-timeline-srv#19 keeps them as negative evidence), so showing one
 * would tell the user the opposite of what they said.
 */
export function ongoingEdges(resolution) {
  const neighbours = Array.isArray(resolution?.neighbours) ? resolution.neighbours : []
  return neighbours
    .filter(n => n && is(n.direction, INBOUND) && is(n.object?.type, 'ongoing') && n.object?.id)
    .filter(n => !is(n.state, 'rejected'))
}

/**
 * The one Ongoing to name on the thread, or null.
 *
 * Confirmed beats suggested, always — "Part of Acme renewal" is a statement about where this mail
 * lives, and a guess must not be dressed up as one. Among equals the most recently confirmed (then
 * the most recently created) wins, because that is the story the user last said yes to.
 *
 * Returns the edge's own shape — `{ id, relationshipId, state, suggested }` — with no title in it:
 * the graph edge carries a reference, not a name, and inventing one here would be a second source
 * of truth for what an Ongoing is called.
 */
export function ongoingLink(resolution) {
  const edges = ongoingEdges(resolution)
  if (!edges.length) return null

  const rank = (edge) => (is(edge.state, 'confirmed') ? 2 : 1)
  const when = (edge) => Date.parse(edge.confirmedUtc || edge.createdUtc || '') || 0

  const best = edges.reduce((a, b) => {
    if (rank(b) !== rank(a)) return rank(b) > rank(a) ? b : a
    return when(b) > when(a) ? b : a
  })

  return {
    id: best.object.id,
    relationshipId: best.relationshipId || null,
    state: String(best.state || ''),
    suggested: !is(best.state, 'confirmed'),
  }
}

/**
 * The line the thread view shows: "Part of Acme renewal", or "Suggested: Acme renewal" while the
 * edge is only a guess. Falls back to a nameless phrasing rather than printing "undefined" — the
 * relationship is a fact even when the Ongoing's title has not loaded yet.
 */
export function ongoingLine(link, title) {
  if (!link) return ''
  const name = String(title || '').trim()
  if (link.suggested) return name ? `Suggested: ${name}` : 'Suggested for an ongoing'
  return name ? `Part of ${name}` : 'Part of an ongoing'
}

/** Where "Open" goes. Empty string for no link, so a template can guard on it. */
export function ongoingPath(link) {
  return link?.id ? `/ongoing/${link.id}` : ''
}

/** Where an email row's "Open" goes — the thread view, keyed by the timeline the thread owns. */
export function threadPath(timelineId) {
  return timelineId ? `/emails/timeline/${timelineId}` : ''
}
